import { type FC, useContext } from 'react';
import {
  CalendarDays,
  CloudSun,
  Leaf,
  MapPin,
  ScrollText,
  Stethoscope,
  Truck,
  UserRoundCheck,
} from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { A2uiSurfaceContext } from '@/app/lib/a2ui/surface-context.ts';
import { useSendIntent } from '@/app/lib/a2ui/blocks/use-send-intent.ts';
import { arr, optStr, str } from '@/app/lib/a2ui/props.ts';

const messages = defineMessages({
  cropAction: {
    id: 'clinicHome.cropAction',
    defaultMessage: 'Start with {crop}',
    description: 'Accessible name for a crop tap that begins a consultation about that crop.',
  },
});

interface Service {
  kind: string;
  label: string;
  hint?: string;
  intent: string;
}

function readServices(value: unknown): Service[] {
  return arr(value).flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) {
      return [];
    }
    const record = entry as Record<string, unknown>;
    const label = str(record.label);
    const intent = str(record.intent);
    return label && intent
      ? [{ kind: str(record.kind), label, hint: optStr(record.hint), intent }]
      : [];
  });
}

function readCrops(value: unknown): string[] {
  return arr(value).flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) {
      return [];
    }
    const name = str((entry as Record<string, unknown>).name);
    return name ? [name] : [];
  });
}

/** The tile's mark comes from `kind`, never from a free-text field: a service
 *  wearing another service's icon reads as a broken app. */
const SERVICE_ICONS: Record<string, FC<{ className?: string; 'aria-hidden'?: boolean }>> = {
  diagnose: Stethoscope,
  weather: CloudSun,
  season: ScrollText,
  advice: CalendarDays,
  inputs: Truck,
  visit: UserRoundCheck,
};

export const ClinicHome: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const surface = useContext(A2uiSurfaceContext);
  const sendIntent = useSendIntent();

  const headline = str(node.props.headline);
  const subheadline = optStr(node.props.subheadline);
  const services = readServices(node.props.services);
  const cropPrompt = optStr(node.props.cropPrompt);
  const crops = readCrops(node.props.crops);
  const promise = optStr(node.props.promise);
  const serviceArea = optStr(node.props.serviceArea);
  const logistics = optStr(node.props.logistics);

  const pickCrop = (crop: string) => {
    surface?.setValue('/clinic/crop', crop);
    surface?.dispatch('selectCrop', { crop });
  };

  return (
    <div className="w-full">
      <header className="animate-fadeUp">
        <h1 className="max-w-[34rem] text-balance text-3xl font-bold leading-[1.15] tracking-tight text-foreground sm:text-4xl">
          {headline}
        </h1>
        {subheadline ? (
          <p className="mt-4 max-w-[34rem] text-base leading-relaxed text-muted-foreground sm:text-body-lg">
            {subheadline}
          </p>
        ) : null}
      </header>

      {/* The whole job, one tap each. */}
      {services.length > 0 ? (
        <div className="mt-7 grid gap-2 sm:grid-cols-2">
          {services.map((service, index) => {
            const Icon = SERVICE_ICONS[service.kind] ?? Leaf;
            return (
              <button
                key={service.label}
                type="button"
                onClick={() => sendIntent(service.intent)}
                style={{ animationDelay: `${index * 60}ms`, touchAction: 'manipulation' }}
                className="flex animate-fadeUp items-center gap-3 rounded-lg border border-border bg-card px-4 py-3.5 text-left transition-[transform,border-color,box-shadow] duration-150 ease-out hover:border-primary hover:shadow-lift focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary active:scale-95"
              >
                <Icon className="size-5 shrink-0 text-primary" aria-hidden />
                <span className="min-w-0">
                  <span className="block font-semibold tracking-tight text-foreground">
                    {service.label}
                  </span>
                  {service.hint ? (
                    <span className="block text-sm leading-snug text-muted-foreground">
                      {service.hint}
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      {/* Secondary way in: name the crop and let the assistant lead. */}
      {crops.length > 0 ? (
        <section className="mt-7">
          {cropPrompt ? (
            <h2 className="mb-2.5 text-sm font-semibold text-muted-foreground">{cropPrompt}</h2>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {crops.map((crop) => (
              <button
                key={crop}
                type="button"
                aria-label={intl.formatMessage(messages.cropAction, { crop })}
                onClick={() => pickCrop(crop)}
                style={{ touchAction: 'manipulation' }}
                className="rounded-md border border-border bg-card px-3.5 py-2 text-sm font-semibold text-foreground transition-[transform,border-color] duration-150 ease-out hover:border-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary active:scale-95"
              >
                {crop}
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {promise ? (
        <p className="mt-8 border-l-2 border-success pl-4 text-base font-medium leading-relaxed text-foreground">
          {promise}
        </p>
      ) : null}

      {serviceArea || logistics ? (
        <div className="mt-8 space-y-2 border-t border-border pt-5">
          {serviceArea ? (
            <p className="flex items-start gap-2 text-sm text-muted-foreground">
              <MapPin className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span className="min-w-0">{serviceArea}</span>
            </p>
          ) : null}
          {logistics ? (
            <p className="flex items-start gap-2 text-sm text-muted-foreground">
              <Truck className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span className="min-w-0">{logistics}</span>
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
